import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Modal } from '@/shared/ui/Modal';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SelectField, TextField } from '@/shared/ui/Field';
import { LifecycleStrip } from '@/shared/ui/LifecycleStrip';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { RunBindingButton } from './RunBindingButton';
import { SchedulingCell, useBindingScheduling } from '@/features/scheduling';
import type { SchedulingSummary } from '@/shared/api/generated/rules';
import { Tabs } from '@/shared/ui/Tabs';
import { Toolbar } from '@/shared/ui/Toolbar';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useRuleDraft, suggestionIdFromTags } from '@/features/rulegen';
import type { RuleDraft as SuggestedRuleDraft } from '@/features/rulegen';
import { SCREEN_PATHS, rulePrefillFrom } from '@/shared/routes';
import { useDatasets } from '@/features/catalog-browser';
import {
  useBindings,
  useCreateBinding,
  useCreateRule,
  useDisableBinding,
  useExecutions,
  usePublishVersion,
  useRuleVersions,
  useRules,
} from '../api/rules';

import type { Execution, Rule, RuleBinding, RuleKind, Severity } from '../api/rules';
import {
  LIFECYCLE_STAGES,
  lastRunOf,
  standingOf,
  reachOf,
  ruleTone,
  shortRunId,
} from '../model/presentation';
import type { BindingStanding, RuleLastRun, RuleReach } from '../model/presentation';
import { AUTHORABLE_KINDS, KIND_SHAPES, invalidParameters } from '../model/parameters';
import { RuleParameterFields } from './RuleParameterFields';

const SEVERITIES: readonly Severity[] = ['info', 'warning', 'error', 'blocker'];

/* Authoring a rule: name, kind, owner. Nothing about what it checks.
 *
 * **Used to also collect the first version's columns and parameters and publish it in the same
 * submit.** `POST /v1/rules` never took them — `RuleService.create` is name/kind/owner/description/
 * tags, full stop, and `RuleExpression` has always lived on `RuleVersion`, a separate aggregate
 * published through its own call. Bundling the two here meant target columns were asked for once
 * on this form and asked for *again* on `PublishVersionForm` the moment a second version was
 * needed — the same field in two places, one of which a reader would not meet until later.
 *
 * **Now this form does the one call `RuleService.create` actually wants**, and publishing stays
 * where the version history already is: `RuleDetail` renders `PublishVersionForm` for any rule
 * still in `draft`, so the very next thing a reader sees after creating one is where to publish its
 * first version — with the columns field asked exactly once, on the form built for it.
 *
 * A rule with no published version is unexecutable (`RuleExpression` lives on the version, and a
 * score is attributed to a version so "a score computed months ago can still be explained" stays
 * true) — that has not changed. What changed is that this form no longer pretends authoring and
 * publishing are one action; `RuleDetail`'s own note said as much before this form caught up to it:
 * "A version freezes an expression, so the form belongs beside the history of what has already been
 * frozen."
 */
/* **Renders the fields and nothing around them — no card, no heading.** Both of this screen's forms
 * used to own a `Card` and sit *inside* the pane, which put a five-field form between the toolbar and
 * the table a reader was looking at. They open in a `Modal` now, which owns the title and hint, so
 * the same copy keys serve both. `AddUserForm` made the same move for the same reason. */
function CreateRuleForm({
  onCreated,
  onCancel,
  draft,
}: {
  readonly onCreated: (rule: Rule) => void;
  /** Dismiss without submitting. Absent renders no Cancel — see `AddUserForm`'s note. */
  readonly onCancel?: (() => void) | undefined;
  /** A suggestion's field values, when the reader arrived from `/suggestions`.
   *
   * **Prefilled, never submitted for them.** §2's whole point is that a recommendation becomes a
   * rule by a person reviewing and saving it, so this fills the fields and presses nothing — and
   * the banner above says the values are proposed, because a form that opens already filled is
   * otherwise indistinguishable from one somebody has already checked.
   */
  readonly draft?: SuggestedRuleDraft | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState(draft?.name ?? '');
  const [owner, setOwner] = useState('');
  const [kind, setKind] = useState<RuleKind>(draft?.kind ?? 'not_null');

  const create = useCreateRule();
  const shape = KIND_SHAPES[kind];

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate(
            /* **The provenance tag and the rationale travel with the create**, and they are the
               reason the second hop works. `POST /v1/rules` already accepts `description` and
               `tags`, so `suggested-from:<id>` needs no schema change to `core.rules` and no
               knowledge of suggestions in that module — and the created rule then carries a durable
               reference the publish form reads back to seed the version's columns and parameters,
               which router state could not survive a reload to do.

               The tag comes from the server on `RuleDraft.tags` rather than being composed here:
               one authority for what the tag says, so the write and the read cannot disagree. */
            draft === undefined
              ? { name, kind, owner }
              : {
                  name,
                  kind,
                  owner,
                  description: draft.description,
                  tags: [...draft.tags],
                },
            { onSuccess: onCreated },
          );
        }}
      >
        {/* **Says the fields are proposed, and it is required rather than decorative.** §11: a
            prefilled form must read as a recommendation to review, not as something already
            checked. `requires_review` is on the wire for the same reason — a screen that decided
            this for itself is a screen that can quietly stop doing it. */}
        {draft !== undefined && draft.requires_review && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="notice" title={t('rules.create.fromSuggestion')}>
              {draft.rationale}
            </Alert>
          </div>
        )}
        {create.isError && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="error">{create.error.message}</Alert>
          </div>
        )}

        <div className="lq-grid">
          <TextField
            label={t('rules.field.name')}
            value={name}
            onChange={setName}
            required
            maxLength={200}
          />
          <SelectField
            label={t('rules.field.kind')}
            value={kind}
            onChange={setKind}
            options={AUTHORABLE_KINDS.map((option) => ({
              value: option,
              label: t(`rules.kind.${option}`),
            }))}
            hint={t(shape.measuresKey)}
          />
          <TextField
            label={t('rules.field.owner')}
            value={owner}
            onChange={setOwner}
            required
            maxLength={200}
            hint={t('rules.field.ownerHint')}
          />
        </div>

        {/* What the columns field used to be here for. Said once, rather than asked twice: this
            form used to collect a version's columns and parameters and publish version 1.0.0 in
            the same submit — the same fields `PublishVersionForm` already asks for on the rule's
            own page, the moment a second version is needed. One field in one place now. */}
        <p className="lq-muted" style={{ marginTop: 'var(--space-3)' }}>
          {t('rules.create.publishSeparately')}
        </p>

        {/* Cancel first in the DOM and on screen: the safe answer is the first tab stop and the one
            that writes goes last. `.lq-form-actions`, not the confirm dialog's row — see
            `AddUserForm` for why those two stay separate. */}
        <div className="lq-form-actions">
          {onCancel === undefined ? null : (
            <Button type="button" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button tone="primary" type="submit" busy={create.isPending}>
            {t('rules.create.submit')}
          </Button>
        </div>
      </form>
    </>
  );
}

export function CreateBindingForm({
  onCreated,
  initialDatasetId,
  initialRuleId,
  onCancel,
}: {
  readonly onCreated: () => void;
  readonly initialDatasetId?: string | undefined;
  readonly initialRuleId?: string | undefined;
  /** Dismiss without submitting. Absent renders no Cancel — `DatasetDetail` owns its own toggle. */
  readonly onCancel?: (() => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const rules = useRules('');
  const datasets = useDatasets({ search: '', connectionId: null });

  const [ruleId, setRuleId] = useState(initialRuleId ?? '');
  const versions = useRuleVersions(ruleId);

  const [ruleVersionId, setRuleVersionId] = useState('');
  const [datasetId, setDatasetId] = useState(initialDatasetId ?? '');
  const [columnName, setColumnName] = useState('');
  const [severity, setSeverity] = useState<Severity>('warning');

  const createBinding = useCreateBinding();

  /* Memoised because `?? []` builds a fresh array on every render, so the effects below would see a
   * changed dependency each time and re-run forever — the warning `react-hooks/exhaustive-deps`
   * raises, and a real one here: the second effect calls `setRuleVersionId` unconditionally, which
   * would be a render loop rather than merely wasted work. */
  const availableRules = useMemo(() => rules.data?.items ?? [], [rules.data]);
  const availableVersions = useMemo(() => versions.data?.items ?? [], [versions.data]);

  const selectedVersion = availableVersions.find((v) => v.id === ruleVersionId);
  const selectedRule = availableRules.find((r) => r.id === ruleId);
  // Only `not_null`/`unique` published with no columns read anything from `explicit_columns` —
  // see `RuleVersion.COLUMNS_OPTIONAL_AT_PUBLISH` — so the field is genuinely inert for every
  // other rule, and saying so up front is better than a value the server silently ignores.
  const columnFieldApplies =
    (selectedRule?.kind === 'not_null' || selectedRule?.kind === 'unique') &&
    (selectedVersion?.expression.columns.length ?? 0) === 0;

  // Pre-select the first rule, so the form is usable without a click.
  useEffect(() => {
    const firstRule = availableRules[0];
    if (!ruleId && firstRule) {
      setRuleId(firstRule.id);
    }
  }, [availableRules, ruleId]);

  // And the newest version of it — cleared when the selected rule has none.
  useEffect(() => {
    const firstVer = availableVersions[0];
    setRuleVersionId(firstVer ? firstVer.id : '');
  }, [availableVersions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ruleId || !ruleVersionId) return;

    const selectors = [];
    if (datasetId) {
      selectors.push({
        kind: 'name_glob' as const,
        description: `Name matches ${datasetId}`,
        pattern: datasetId,
      });
    }
    if (selectors.length === 0) {
      selectors.push({
        kind: 'name_glob' as const,
        description: 'Name matches *',
        pattern: '*',
      });
    }

    // The dataset dropdown already picked *which* table; "Target Column" says *which column* on
    // it, which a `name_glob` selector has no other way to answer — see
    // `RuleBinding.explicit_columns` on the backend. Not a selector: typing a business column like
    // `salary` into a `column_role` selector used to fail validation outright, since roles are a
    // closed, PII-shaped enum.
    const explicitColumns = columnFieldApplies
      ? columnName
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean)
      : [];

    createBinding.mutate(
      {
        ruleId,
        ruleVersionId,
        selectors,
        severity,
        explicitColumns,
      },
      {
        onSuccess: onCreated,
      },
    );
  };

  const datasetOptions = [
    { value: '*', label: '* (All Datasets)' },
    ...(datasets.data?.items ?? []).map((row) => ({
      value: `${row.schema_name}.${row.object_name}`,
      label: `${row.schema_name}.${row.object_name}`,
    })),
  ];

  const ruleOptions = availableRules.map((r) => ({
    value: r.id,
    label: `${r.name} (${r.kind})`,
  }));

  const versionOptions = availableVersions.map((v) => ({
    value: v.id,
    label: `v${v.version}`,
  }));

  return (
    <>
      <form onSubmit={handleSubmit}>
        {createBinding.isError && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="error">{createBinding.error.message}</Alert>
          </div>
        )}

        <div
          className="lq-grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          <SelectField
            label={t('rules.bindings.create.ruleLabel')}
            value={ruleId}
            onChange={(val) => {
              setRuleId(val);
            }}
            options={ruleOptions}
          />

          <SelectField
            label={t('rules.bindings.create.versionLabel')}
            value={ruleVersionId}
            onChange={(val) => {
              setRuleVersionId(val);
            }}
            options={
              versionOptions.length > 0
                ? versionOptions
                : [{ value: '', label: t('rules.bindings.noVersion') }]
            }
          />

          <SelectField
            label={t('rules.bindings.create.datasetLabel')}
            value={datasetId}
            onChange={(val) => {
              setDatasetId(val);
            }}
            options={datasetOptions}
          />

          <TextField
            label={t('rules.bindings.create.columnLabel')}
            value={columnName}
            onChange={setColumnName}
            placeholder="e.g. email, status"
            hint={
              columnFieldApplies
                ? t('rules.bindings.create.columnHint.applies')
                : t('rules.bindings.create.columnHint.inert')
            }
          />

          <SelectField
            label={t('rules.bindings.create.severityLabel')}
            value={severity}
            onChange={(val) => {
              setSeverity(val);
            }}
            options={SEVERITIES.map((s) => ({
              value: s,
              label: t(`rules.severity.${s}.label`),
            }))}
          />
        </div>

        <div className="lq-form-actions">
          {onCancel === undefined ? null : (
            <Button type="button" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            tone="primary"
            type="submit"
            busy={createBinding.isPending}
            disabled={!ruleId || !ruleVersionId}
          >
            {t('rules.bindings.create.submit')}
          </Button>
        </div>
      </form>
    </>
  );
}

function nextPatchVersion(ver: string): string {
  const parts = ver.split('.').map((p) => parseInt(p, 10));
  const major = parts[0];
  const minor = parts[1];
  const patch = parts[2];
  if (
    parts.length === 3 &&
    major !== undefined &&
    minor !== undefined &&
    patch !== undefined &&
    !isNaN(major) &&
    !isNaN(minor) &&
    !isNaN(patch)
  ) {
    return `${String(major)}.${String(minor)}.${String(patch + 1)}`;
  }
  return `${ver}.1`;
}

export function PublishVersionForm({
  rule,
  onPublished,
}: {
  readonly rule: Rule;
  readonly onPublished: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const versions = useRuleVersions(rule.id);
  const items = versions.data?.items ?? [];
  const latestVersion = items[0];
  const suggestedVersion = latestVersion
    ? nextPatchVersion(latestVersion.version)
    : '1.0.0';

  const [version, setVersion] = useState(suggestedVersion);
  const [columnsStr, setColumnsStr] = useState('');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [notes, setNotes] = useState('Published version update');

  /* **Seeded from the latest version once the versions arrive**, which is what makes this a
   * *revision* rather than a re-entry: publishing 1.0.1 of a pattern rule almost always means
   * changing the pattern, and retyping the other parameters is how one gets accidentally dropped.
   *
   * In an effect rather than `useState`'s initialiser, and that is a fix rather than a preference —
   * the initialiser runs on the first render, when `useRuleVersions` is still in flight, so it
   * captured an empty object and the field rendered blank. Found by reading the rendered input's
   * value rather than the code, which is the only place it shows.
   *
   * **Guarded on `parameters` being empty**, so it seeds once and never overwrites typing. An author
   * who clears a field leaves `{ pattern: '' }` behind — non-empty — so even a deliberately emptied
   * field is not re-filled behind them. */
  useEffect(() => {
    const carried = latestVersion?.expression.parameters;
    if (carried === undefined) return;
    setParameters((current) =>
      Object.keys(current).length === 0 ? { ...carried } : current,
    );
  }, [latestVersion]);

  /* **The second half of §2's loop, and the provenance tag is what makes it possible.**
   *
   * Creating a rule from a suggestion is two calls — `POST /v1/rules` takes the name, kind and
   * owner, and the columns and parameters live on the *version* — so the suggestion's values have
   * to survive the hop. Router state does not: a reload, a bookmark or a link shared with the
   * second pair of eyes all arrive here with nothing, and the author retypes exactly the values the
   * recommendation existed to supply.
   *
   * So the created rule carries `suggested-from:<id>` in its own `tags`, and this reads it back.
   * `core.rules` needed no schema change and knows nothing about suggestions; the tag is a
   * reference, and reading it here is what turns it from a label into a mechanism.
   *
   * **Only for a rule with no version yet.** `latestVersion` is the stronger source once one
   * exists — publishing 1.0.1 is a revision of what was frozen, not a re-entry of what was
   * originally recommended — and seeding from the suggestion then would silently revert an author's
   * earlier edit under a version label that reads like a patch.
   *
   * Guarded on empty for the effect above's own reason: it fills once and never overwrites typing,
   * so a field an author deliberately cleared is not re-filled behind them. */
  const suggestionId = suggestionIdFromTags(rule.tags);
  const seed = useRuleDraft(
    versions.isSuccess && items.length === 0 && suggestionId !== null
      ? suggestionId
      : undefined,
  );
  useEffect(() => {
    if (!seed.isSuccess) return;
    const proposed = seed.data;
    setColumnsStr((current) => (current === '' ? proposed.columns.join(', ') : current));
    setParameters((current) =>
      Object.keys(current).length === 0 ? { ...proposed.parameters } : current,
    );
  }, [seed.isSuccess, seed.data]);

  /* **The columns are carried forward too, and their absence was a silent defect.** The effect
   * above seeded `parameters` on the argument that "retyping the other parameters is how one gets
   * accidentally dropped" — and `columns` is subject to exactly that and was not seeded, so
   * publishing 1.0.1 of a rule carried its pattern and silently lost the column the pattern was
   * written against.
   *
   * It did not fail loudly either, which is what made it worth fixing rather than noting.
   * `COLUMNS_OPTIONAL_AT_PUBLISH` lets `not_null` and `unique` publish with none, so for those two
   * the revision was accepted and the new version measured whatever `ExecutionService.plan`
   * resolves from the dataset's schema instead of the named column — a different measurement under
   * a version label that reads like a patch. For the other kinds the server refused it, which is
   * the better half of the same bug.
   *
   * Guarded on empty for the seeding effect's own reason: it fills once and never overwrites
   * typing, so an author who deliberately clears the field is not re-filled behind them. */
  useEffect(() => {
    const carried = latestVersion?.expression.columns;
    if (carried === undefined) return;
    setColumnsStr((current) => (current === '' ? carried.join(', ') : current));
  }, [latestVersion]);

  useEffect(() => {
    if (suggestedVersion) {
      setVersion(suggestedVersion);
    }
  }, [suggestedVersion]);

  const publish = usePublishVersion();

  const handlePublish = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    /* Refused here rather than sent. The server's message is clear — "pattern requires parameter(s)
       pattern; a rule that cannot be executed must not be publishable" — and an author reads it far
       better beside the field than as a banner over a form whose field they then have to find. */
    if (invalidParameters(rule.kind, parameters).size > 0) return;

    const columns = columnsStr
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    publish.mutate(
      {
        ruleId: rule.id,
        version: version.trim() || suggestedVersion,
        expression: {
          kind: rule.kind,
          columns,
          parameters,
        },
        notes,
      },
      {
        onSuccess: onPublished,
      },
    );
  };

  return (
    <Card
      title={`${t('rules.version.publish.title')} — ${rule.name}`}
      hint={
        /* **Three hints, and the third is load-bearing.** This form is now reachable for an active
           rule, where the question a steward has is not "what is the latest version" but "am I
           about to change production". They are not: a binding pins an exact `rule_version_id`
           rather than following latest, so a new version is inert until somebody rebinds. Saying so
           here rather than only in a code comment, because the person who needs it is reading the
           screen. */
        latestVersion && rule.state === 'active'
          ? t('rules.version.publish.liveRule', {
              version: latestVersion.version,
              kind: t(`rules.kind.${rule.kind}`),
            })
          : latestVersion
            ? t('rules.version.publish.latest', {
                version: latestVersion.version,
                kind: t(`rules.kind.${rule.kind}`),
              })
            : t('rules.version.publish.kindOnly', { kind: t(`rules.kind.${rule.kind}`) })
      }
    >
      <form onSubmit={handlePublish}>
        {publish.isError && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <Alert tone="error">{publish.error.message}</Alert>
          </div>
        )}
        <div className="lq-grid">
          <TextField
            label={t('rules.version.publish.versionLabel')}
            value={version}
            onChange={setVersion}
            required
            hint={
              latestVersion
                ? t('rules.version.publish.versionHint', {
                    latest: latestVersion.version,
                  })
                : t('rules.version.publish.versionFirstHint')
            }
          />
          <TextField
            label={t('rules.version.publish.columnsLabel')}
            value={columnsStr}
            onChange={setColumnsStr}
            placeholder={t('rules.field.columnsPlaceholder')}
          />
          <TextField
            label={t('rules.version.publish.notesLabel')}
            value={notes}
            onChange={setNotes}
          />
        </div>

        {/* **The kind's own parameters, which this form could not ask for at all.** It sent
            `parameters: {}` unconditionally, so `pattern`, `range`, `accepted_values`, `freshness`,
            `referential_integrity` and `custom_sql` were refused by the server with a message no
            field on screen could answer — a shipped dead end for six of the nine kinds. The fields
            come from `KIND_SHAPES`, the same table the author form reads, so the two cannot ask for
            different things. */}
        <RuleParameterFields
          kind={rule.kind}
          values={parameters}
          onChange={(parameter, value) => {
            setParameters((current) => ({ ...current, [parameter]: value }));
          }}
          submitted={submitted}
        />
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button tone="primary" type="submit" busy={publish.isPending}>
            {t('rules.version.publish.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* What a rule is bound to, in a cell.
 *
 * Four readings and four sentences — see `RuleReach`. The one that matters is `unresolved`: a rule
 * with bindings that no recent run has resolved has an *unknown* dataset count, not a count of zero,
 * and rendering `0 datasets` there would say a steward's binding reaches nothing.
 */
function ReachCell({ reach }: { readonly reach: RuleReach }): ReactElement {
  const { t } = useTranslation();

  if (reach.kind === 'unread') {
    return (
      <span className="lq-muted" title={t('rules.reach.unread.meaning')}>
        {t('rules.reach.unread.label')}
      </span>
    );
  }
  if (reach.kind === 'unbound') {
    /* An em dash, matching the reference, with the reason on hover. A rule with no binding runs
       against nothing however active it is, which is the state this cell exists to make visible. */
    return (
      <span className="lq-muted" title={t('rules.reach.unbound.meaning')}>
        &mdash;
      </span>
    );
  }
  if (reach.kind === 'unresolved') {
    return (
      <span title={t('rules.reach.unresolved.meaning')}>
        {t('rules.reach.unresolved.label', { count: reach.bindings })}
      </span>
    );
  }
  return (
    <span title={t('rules.reach.resolved.meaning', { count: reach.bindings })}>
      {t('rules.reach.resolved.label', { count: reach.datasets })}
    </span>
  );
}

/* When a rule last measured anything, or why it never has.
 *
 * **The never-run case is explained by the rule's own state**, which is the whole value of the
 * column: a draft has not been published, a published rule has not been sent for approval, one
 * awaiting approval is not active yet. Three different next steps, where a bare "Never" sends a
 * reader looking for an execution that could not have existed.
 */
function LastRunCell({ lastRun }: { readonly lastRun: RuleLastRun }): ReactElement {
  const { t } = useTranslation();

  if (lastRun.kind === 'unread') {
    return (
      <span className="lq-muted" title={t('rules.lastRun.unread.meaning')}>
        {t('rules.lastRun.unread.label')}
      </span>
    );
  }
  if (lastRun.kind === 'never') {
    return (
      <span className="lq-muted">{t(`rules.lastRun.never.${lastRun.because}`)}</span>
    );
  }
  if (lastRun.kind === 'beyondWindow') {
    /* Active, bound, and absent from the runs this screen read. Deliberately not "never": the
       executions listing is bounded, so the honest statement is about our window rather than about
       the rule's history. */
    return (
      <span className="lq-muted" title={t('rules.lastRun.beyondWindow.meaning')}>
        {t('rules.lastRun.beyondWindow.label')}
      </span>
    );
  }
  return (
    <span title={t('rules.lastRun.ran.meaning', { at: new Date(lastRun.at) })}>
      {t('rules.lastRun.ran.label', {
        at: new Date(lastRun.at),
        count: lastRun.datasets,
      })}
    </span>
  );
}

type RulesPane = 'rules' | 'bindings';

/* One rule as a row. **It holds no mutation now**, which is the point of the change that moved the
 * actions to the rule's own page: a row that cannot act needs no error state, no fragment wrapper and
 * no busy control, so the whole component is markup over props. */
function RuleRow({
  rule,
  bindings,
  executions,
}: {
  readonly rule: Rule;
  /** `undefined` while the bindings read is in flight or failed — see `RuleReach.unread`. */
  readonly bindings: readonly RuleBinding[] | undefined;
  readonly executions: readonly Execution[] | undefined;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <tr>
      <td>
        {/* The owner moves here as the secondary line, beside the kind. It was a column of names
              nobody scans, and it is still the thing an on-call engineer needs at 03:00 — so it stays
              on the row rather than being dropped. */}
        <PrimaryCell secondary={`${t(`rules.kind.${rule.kind}`)} \u00b7 ${rule.owner}`}>
          {rule.name}
        </PrimaryCell>
      </td>
      <td>
        <StatusBadge
          tone={ruleTone(rule.state)}
          label={t(`rules.state.${rule.state}.label`)}
          title={t(`rules.state.${rule.state}.meaning`)}
        />
      </td>
      <td>
        <ReachCell reach={reachOf(rule, bindings, executions)} />
      </td>
      <td>
        <LastRunCell lastRun={lastRunOf(rule, bindings, executions)} />
      </td>
      {/* **`Open` rather than two action buttons**, matching the reference and right on the
            merits: activation is a two-eyes transition the platform refuses with a 409 naming what is
            missing, and a list row has nowhere to put that reason and nothing to say about which
            version is being activated. Both controls moved to the rule's own page, which is where
            this link goes. */}
      <td className="lq-table__actions">
        <Link className="lq-cell__link" to={`${SCREEN_PATHS.rules}/${rule.id}`}>
          {t('rules.open')}
        </Link>
      </td>
    </tr>
  );
}

/* A binding's standing, in words rather than a badge.
 *
 * Five readings and five sentences - see `BindingStanding`. The two that matter most are `disabled`,
 * which is the binding's own switch and overrides whatever the rule's state says, and `unread`, which
 * is what a bounded rules read produces for a binding whose rule is past the page.
 */
function StandingCell({
  standing,
}: {
  readonly standing: BindingStanding;
}): ReactElement {
  const { t } = useTranslation();

  if (standing.kind === 'unread') {
    return (
      <span className="lq-muted" title={t('rules.bindings.standing.unread.meaning')}>
        {t('rules.bindings.standing.unread.label')}
      </span>
    );
  }
  if (standing.kind === 'disabled') {
    return (
      <span className="lq-muted" title={t('rules.bindings.standing.disabled.meaning')}>
        {t('rules.bindings.standing.disabled.label')}
      </span>
    );
  }
  if (standing.kind === 'ruleNotActive') {
    return (
      <span className="lq-muted">
        {t(`rules.bindings.standing.notActive.${standing.state}`)}
      </span>
    );
  }
  if (standing.kind === 'notRunYet') {
    return (
      <span className="lq-muted" title={t('rules.bindings.standing.notRunYet.meaning')}>
        {t('rules.bindings.standing.notRunYet.label')}
      </span>
    );
  }
  return (
    <span>{t('rules.bindings.standing.running', { at: new Date(standing.at) })}</span>
  );
}

function BindingRow({
  binding,
  rule,
  executions,
  scheduling,
  selected,
  onQueued,
}: {
  readonly binding: RuleBinding;
  /** `undefined` when the bounded rules read did not include this binding's rule. */
  readonly rule: Rule | undefined;
  readonly executions: readonly Execution[] | undefined;
  /** This binding's scheduling answer, or `undefined` when the page's one read has not landed.
   *
   * Passed down rather than fetched here: a cell that fetches is a request per row, which is what
   * exhausted the API's connection pool. */
  readonly scheduling: SchedulingSummary | undefined;
  /** True for the row whose run was just queued, so the banner above has a row to point at. */
  readonly selected: boolean;
  readonly onQueued: (execution: { readonly id: string }, binding: RuleBinding) => void;
}): ReactElement {
  const { t } = useTranslation();
  const disable = useDisableBinding();

  return (
    <tr className={selected ? 'lq-row--live' : undefined}>
      <td>
        {/* The rule's name, because a row reading only "name matches ecom.customer" never said
         *which rule* applies there — and the rule is the subject a reader is looking for. */}
        {rule?.name ?? (
          <span className="lq-muted">{t('rules.bindings.ruleUnknown')}</span>
        )}
      </td>
      <td className="lq-mono lq-cell--quiet">
        {/* The selector's own description, which the server writes: "tagged pii" is answerable a year
            later where "the selector matched" is not — the argument `ResolvedTarget.matched_by`
            makes for an execution. The scope goes on a second mono line, as the reference sets it. */}
        <PrimaryCell secondary={bindingScope(binding, t)}>
          {binding.selectors.map((selector) => selector.description).join(' or ')}
        </PrimaryCell>
      </td>
      <td className="lq-cell--quiet">
        {/* **This column used to read "On request only" on every row, and the comment here used to
            explain that nothing in the platform scheduled a rule run.** That was true and is not:
            `PLATFORM_SCHEDULES` now carries `rules.dispatch_due_executions` and
            `rules.follow_harvests`, so a binding can be time-driven, harvest-driven, both or
            neither — and the placeholder had become the stale-prose defect it was written to avoid.

            Two labelled lines rather than two columns, because the question a reader scans this
            table for is one question: does this binding run without me. The full cards, with the
            forms and every state, are on the rule's own page. */}
        <SchedulingCell scheduling={scheduling} />
      </td>
      <td className="lq-cell--quiet">
        {/* **What a violation is recorded as, not what happens next.** The reference's values are
            actions — raise an incident, record only, block the downstream load — and a binding
            carries none of those: it carries a `severity`, and what reaches whom is the tenant's own
            alert routing, configured on a different screen. Naming this column's content after the
            action would promise a routing decision this row does not make. */}
        {t(`rules.severity.${binding.severity}.label`)}
      </td>
      <td>
        <StandingCell standing={standingOf(binding, rule, executions)} />
      </td>
      <td className="lq-table__actions">
        {/* **Run before Unbind**, because the two are not peers: running is the thing a steward does
            repeatedly and unbinding is the thing they do once, and putting the destructive-sounding
            control first is how it gets pressed by mistake in a dense table.

            Link-styled and separated by a rule, as the reference has them: six bordered buttons down
            a column reads as a form, and these are two verbs on a row of facts. */}
        <span className="lq-rowactions">
          <RunBindingButton
            appearance="link"
            bindingId={binding.id}
            enabled={binding.enabled}
            onQueued={(execution) => {
              onQueued(execution, binding);
            }}
          />
          {binding.enabled ? (
            <>
              <span className="lq-rowactions__rule" aria-hidden="true" />
              {/* **"Unbind", the reference's word and the more accurate one.** The mutation is
                  `POST /bindings/{id}/disable` and the control said "Disable" — but a disabled
                  binding is indistinguishable from an unbound one to every reader of this screen,
                  and "unbind" says what it does to the rule: it stops running against that target.
                  The footnote below carries what it does not do. */}
              <button
                type="button"
                className="lq-rowaction"
                disabled={disable.isPending}
                onClick={() => {
                  disable.mutate({
                    bindingId: binding.id,
                    reason: 'Unbound from the Rules screen',
                  });
                }}
              >
                {t('rules.bindings.unbind')}
              </button>
            </>
          ) : (
            <span className="lq-muted">{t('rules.bindings.disabled')}</span>
          )}
        </span>
      </td>
    </tr>
  );
}

/** What of the target a binding reads.
 *
 * The reference's second mono line — "Whole dataset", or the filter. A binding carries no row filter
 * in this platform, so every one of them reads the whole thing, and saying so is the point: it is the
 * difference between a rule that samples and one that scans, which is what a reader is checking for
 * before pressing Run on a production table.
 */
function bindingScope(binding: RuleBinding, t: (key: string) => string): string {
  return binding.selectors.length > 1
    ? t('rules.bindings.scope.anyMatch')
    : t('rules.bindings.scope.whole');
}

export function RulesScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  /* **A suggestion arriving from `/suggestions`, or `null` for an ordinary visit.** Read once on
     mount rather than on every render: a reader who closes the form and opens it again is
     authoring by hand, and re-seeding from state that is still sitting in the history entry would
     put the suggestion's values back into a form they had just cleared. */
  const [prefill] = useState(() => rulePrefillFrom(location.state));
  const draft = useRuleDraft(prefill?.suggestionId);
  const [formOpen, setFormOpen] = useState(false);

  /* **Opened once the draft has resolved, not on arrival**, and that ordering is a fix rather than
     a nicety. `Modal` unmounts its subtree while closed, so `CreateRuleForm`'s `useState`
     initialisers run at the moment it opens — open it immediately and they capture an
     `undefined` draft and the fields render blank for ever, which is exactly the defect
     `PublishVersionForm`'s seeding effect below was written for after it happened there. Waiting
     means the initialiser sees the values and no seeding effect is needed at all.

     `isError` opens it too: a suggestion regenerated away 404s, and the right answer is a blank
     authoring form rather than a modal that never appears and a button that looks broken.

     **Fires once**, and the latch is load-bearing rather than tidy: `isSuccess` stays true for the
     life of the query, so without it a reader who closed the form would have it reopened on the
     very next render — a modal that cannot be dismissed. */
  const [offered, setOffered] = useState(false);
  useEffect(() => {
    if (offered || prefill === null) return;
    if (draft.isSuccess || draft.isError) {
      setOffered(true);
      setFormOpen(true);
    }
  }, [offered, prefill, draft.isSuccess, draft.isError]);
  const [bindingFormOpen, setBindingFormOpen] = useState(false);
  /* The run just queued from a binding row, and the binding it was for. Held as a pair rather than
     an id, because the banner names the rule and the target and re-deriving them from the id would
     mean searching the list for a row the reader is already looking at. `null` is the ordinary
     state — the banner is not a permanent part of the pane. */
  const [queued, setQueued] = useState<{
    readonly execution: { readonly id: string };
    readonly binding: RuleBinding;
  } | null>(null);

  const rules = useRules(search);
  /* Which table is showing. Local state rather than a URL parameter, for the reason the tab strip's
   * own comment gives: a binding has no meaning without its rule and both forms above are shared, so
   * a second address would be the same page state under a different name. */
  const [pane, setPane] = useState<RulesPane>('rules');
  const bindings = useBindings();
  /* Recent executions, for the two derived columns. **Not a request per rule**: `Execution.targets`
   * carries the resolved rule-version-to-dataset set for a whole run, so one read answers both
   * columns for every row — see `reachOf`. Shares the Executions screen's cache entry, so arriving
   * here from there costs nothing. */
  const executions = useExecutions();
  /* Scheduling for the whole page in one read, for the same reason as the line above — and this
   * one is not a preference. `SchedulingCell` used to fetch its own schedule and trigger, so a
   * table of N rows opened 2N concurrent requests and each held a database session for its whole
   * life. Eight bindings exhausted the API's connection pool and everything past it raised after
   * the checkout timeout. The request count is now a constant. */
  const scheduling = useBindingScheduling(
    bindings.isSuccess ? bindings.data.items.map((binding) => binding.id) : [],
  );

  /* Built once per render rather than inline, so the strip's JSX is one line and the five stages are
   * read from one place. `title` carries the stage-versus-state distinction: `LifecycleStrip` has no
   * slot for it, and adding one would be a prop only this caller ever sets. */
  const lifecycleSteps = LIFECYCLE_STAGES.map((stage) => ({
    label: t(stage.labelKey),
    /* Every stage is drawn as reached: this is the product's lifecycle, not one rule's position, and
     * hollowing them would imply no rule had ever got that far. */
    reached: true,
    colour: stage.hue,
  }));

  return (
    <>
      <PageHeader
        title={t('rules.title')}
        lede={t('rules.lede')}
        /* **One action, and it belongs to the tab that is showing.** Both forms lived up here, so
           the Rules tab offered "Bind a rule to a dataset" — an action about the *other* tab's
           subject — and a reader had to know which of two buttons applied to what they were looking
           at. A binding is created from the Bindings tab and a rule from the Rules tab, which is
           also what makes each button's label unambiguous without naming its object twice. */
        /* **Not a toggle any more, and that is the fix rather than a simplification.** Each button
           used to flip its own label to Cancel and render an inline panel *inside its pane* — and
           the binding panel was rendered in the `rules` branch while its button lives in the
           `bindings` one, so pressing "Bind a rule" flipped the label to Cancel and put the form in
           the branch that is not on screen. A form that opens into a hidden tab, with the only
           feedback being a button that now says Cancel. A dialog has one home wherever it is
           triggered from, and closes through its own controls. */
        actions={
          pane === 'rules' ? (
            <Button
              tone="primary"
              onClick={() => {
                setFormOpen(true);
              }}
            >
              {t('rules.create.open')}
            </Button>
          ) : (
            <Button
              tone="primary"
              onClick={() => {
                setBindingFormOpen(true);
              }}
            >
              {t('rules.bindings.create.open')}
            </Button>
          )
        }
      />

      {/* **Two tabs where this was two stacked cards.** The rules table and the bindings table are
          different subjects — what a rule *is* against where it *applies* — and stacking them meant
          a steward scrolled past every rule to reach the bindings, on a screen whose two halves are
          equally often the reason somebody opened it.

          Not a route each. A binding has no meaning without its rule and the two forms above are
          shared, so a second URL would need the same page state under a different address.
          `aria-controls` is what `Tabs` already wires; the panels are siblings below. */}
      <Tabs
        label={t('rules.tabs.label')}
        active={pane}
        onSelect={setPane}
        /* **No counts.** Each tab's own content already states its total — the Rules table has "1
           rule" beside its search, and the bindings pane its own hint — so a count here was a second
           rendering of one figure that can disagree with the first while a read is in flight. */
        tabs={[
          { id: 'rules', label: t('rules.tab.rules') },
          { id: 'bindings', label: t('rules.tab.bindings') },
        ]}
      />

      {/* Conditionally rendered rather than `hidden`, matching `DatasetDetail`'s panels. `hidden`
          keeps the other table mounted — so every visit to one pane paid for the other's rows, and a
          screen reader's table list held two tables where the reader can see one. */}
      {pane === 'rules' ? (
        <div className="lq-stack">
          {/* The lifecycle, before the table. A steward whose rule is not running needs to see that
            `active` is two gates away from `draft` — the platform refuses the activation with a 409
            whose reason is "a second person is missing", and that only makes sense against a
            visible sequence. Three states because the API has three; publishing and two-eyes are
            gates rather than states, and `LifecycleStrip` renders them as such. */}
          {/* **Five steps, and two of them are not `RuleState` values.** `RuleState` has three —
              draft, active, deprecated — so an earlier version of this strip drew three states with
              the other two as *gates* between them, on the argument that a badge can never read
              "Published".

              That argument was about the wrong subject. This strip is the product's lifecycle rather
              than one rule's position, and publishing a version and requesting approval are real,
              observable stages a reader has to pass through: `usePublishVersion` produces a
              `RuleVersion`, and a transition request sits in `kernel.approval` waiting for a second
              signature. Drawing them as gate labels made the journey read as two steps when it is
              four.

              What keeps it honest is `LIFECYCLE_STEPS`, where each step says whether it is a state a
              rule row can hold — and the two that are not carry that on hover, so nobody goes
              looking for a `Published` badge. */}
          <LifecycleStrip steps={lifecycleSteps} note={t('rules.lifecycle.note')} />

          <div>
            <Toolbar
              count={
                rules.isSuccess
                  ? t('rules.count', { count: rules.data.items.length })
                  : undefined
              }
            >
              <TextField
                label={t('rules.searchLabel')}
                type="search"
                value={search}
                onChange={setSearch}
                placeholder={t('rules.searchPlaceholder')}
              />
            </Toolbar>

            {rules.isError && (
              <Alert tone="error" title={t('rules.unreachable')}>
                {rules.error.message}
              </Alert>
            )}

            {rules.isPending && (
              <>
                <p role="status" className="lq-muted">
                  {t('rules.loading')}
                </p>
                <SkeletonRows rows={5} columns={4} />
              </>
            )}

            {rules.isSuccess &&
              (rules.data.items.length === 0 ? (
                <EmptyState
                  title={
                    search.trim().length > 0
                      ? t('rules.noMatches')
                      : t('rules.emptyTitle')
                  }
                  guidance={
                    search.trim().length > 0
                      ? t('rules.noMatchesGuidance')
                      : t('rules.emptyGuidance')
                  }
                  action={
                    <Button
                      tone="primary"
                      onClick={() => {
                        setFormOpen(true);
                      }}
                    >
                      {t('rules.create.open')}
                    </Button>
                  }
                />
              ) : (
                <Card flush>
                  <DataTable
                    caption={t('rules.tableCaption')}
                    columns={[
                      { key: 'name', label: t('rules.column.rule') },
                      { key: 'state', label: t('rules.column.state') },
                      /* **`Bound to` and `Last run` replace `owner`.** The owner is on the rule's own
                       page and is a name, not a state — a column of names is the one thing a reader
                       scanning a rules list never acts on, where "reaches nothing" and "has never
                       run" are both actionable. */
                      { key: 'reach', label: t('rules.column.boundTo') },
                      { key: 'lastRun', label: t('rules.column.lastRun') },
                      { key: 'actions', label: '', actions: true },
                    ]}
                  >
                    {rules.data.items.map((rule) => (
                      <RuleRow
                        key={rule.id}
                        rule={rule}
                        bindings={bindings.isSuccess ? bindings.data.items : undefined}
                        executions={
                          executions.isSuccess ? executions.data.items : undefined
                        }
                      />
                    ))}
                  </DataTable>
                </Card>
              ))}
          </div>
        </div>
      ) : null}

      {pane === 'bindings' ? (
        <div className="lq-stack">
          {/* The reference's lede, and the sentence this screen most needed. "An active rule with
              no binding never executes" is the commonest confusion about this model — a steward
              activates a rule, waits, and nothing runs — and it belongs above the table rather than
              in a tooltip on it. */}
          <p className="lq-pane__lede">{t('rules.bindings.lede')}</p>

          {/* **What the run queued, said in place.** The reference reports it here rather than
              navigating, and that is right for this screen: a reader is working *down* a ledger and
              may want to run three rules, so being thrown onto a run's page after the first one
              loses their place to show them something a link already reaches. The banner names the
              run, the rule and the target, states that it is a one-off, and offers the way through.
              */}
          {queued === null ? null : (
            <QueuedRun
              queued={queued}
              onDismiss={() => {
                setQueued(null);
              }}
            />
          )}

          <div className="lq-bindings">
            {bindings.isError && (
              <div className="lq-card__body">
                <Alert tone="error">{bindings.error.message}</Alert>
              </div>
            )}
            {bindings.isSuccess &&
              (bindings.data.items.length === 0 ? (
                <div className="lq-card__body">
                  <p className="lq-muted" style={{ margin: 0 }}>
                    {t('rules.bindings.empty')}
                  </p>
                </div>
              ) : (
                <DataTable
                  variant="ledger"
                  caption={t('rules.bindings.tableCaption')}
                  columns={[
                    /* **Four columns, where the reference has six.** `Runs` (Daily 06:00 / After
                       each harvest / On request only) **is now real and is the `scheduling`
                       column below**: steps 3 and 4 gave a binding an execution schedule and a
                       harvest trigger, so the fact this comment used to say the platform did not
                       model is stored in two tables and rendered per row.

                       `On failure` (Raise an incident / Record only / Block the downstream load) is
                       still absent, and for the original reason: a binding carries no failure
                       action. Rendering it would be inventing a fact per row, on the screen a
                       steward uses to decide what runs.

                       `severity` is the nearest real thing to "on failure" and is a
                       *classification* rather than an action — what happens is the tenant's alert
                       routing — so it sits on the rule's secondary line rather than under a heading
                       promising more than it carries. */
                    { key: 'rule', label: t('rules.bindings.column.rule') },
                    { key: 'target', label: t('rules.bindings.column.target') },
                    { key: 'scheduling', label: t('rules.bindings.column.scheduling') },
                    { key: 'onFailure', label: t('rules.bindings.column.onFailure') },
                    { key: 'standing', label: t('rules.bindings.column.standing') },
                    { key: 'actions', label: '', actions: true },
                  ]}
                >
                  {bindings.data.items.map((binding) => (
                    <BindingRow
                      key={binding.id}
                      binding={binding}
                      rule={rules.data?.items.find((each) => each.id === binding.rule_id)}
                      executions={
                        executions.isSuccess ? executions.data.items : undefined
                      }
                      scheduling={
                        scheduling.isSuccess ? scheduling.data.get(binding.id) : undefined
                      }
                      selected={queued?.binding.id === binding.id}
                      onQueued={(execution, forBinding) => {
                        setQueued({ execution, binding: forBinding });
                      }}
                    />
                  ))}
                </DataTable>
              ))}
          </div>

          {/* The reference's footnote, and it is the half the button cannot carry: a reader about to
              press Unbind needs to know it is not a delete. */}
          <p className="lq-muted lq-table__footnote">{t('rules.bindings.footnote')}</p>
        </div>
      ) : null}

      {/* **Both dialogs live here, outside either pane.** A modal is in the top layer and belongs to
          the screen rather than to the tab that opened it — rendering one inside a pane is what put
          the binding form in the branch that was not on screen. Mounted only while open, so a second
          opening starts from a clean form rather than the last attempt's values. */}
      <Modal
        open={formOpen}
        title={t('rules.create.title')}
        hint={t('rules.create.hint')}
        onClose={() => {
          setFormOpen(false);
        }}
      >
        <CreateRuleForm
          /* `draft.data` only on success — a failed or in-flight read yields `undefined`, which is
             an ordinary blank form rather than a broken one. That is the safe direction here: a
             suggestion that has since been regenerated away 404s, and the reader can still author
             the rule by hand. */
          draft={draft.isSuccess ? draft.data : undefined}
          onCreated={(rule) => {
            setFormOpen(false);
            /* Straight to the rule that was just created — `PublishVersionForm` is the very next
               thing on that page for anything still `draft`, which every new rule is. Landing on
               the list instead would leave "how do I give this an expression" to be rediscovered. */
            navigate(`${SCREEN_PATHS.rules}/${rule.id}`);
          }}
          onCancel={() => {
            setFormOpen(false);
          }}
        />
      </Modal>

      <Modal
        open={bindingFormOpen}
        title={t('rules.bindings.create.title')}
        hint={t('rules.bindings.create.datasetHint')}
        onClose={() => {
          setBindingFormOpen(false);
        }}
      >
        <CreateBindingForm
          onCreated={() => {
            setBindingFormOpen(false);
          }}
          onCancel={() => {
            setBindingFormOpen(false);
          }}
        />
      </Modal>
    </>
  );
}

/* What a Run now press queued, reported where the reader pressed it.
 *
 * **Four statements, and each is one a reader would otherwise have to assume.** Which run it is, so
 * they can find it. What it will do — this rule, against this target, reading the whole thing. That
 * it is a **one-off**, which is the reassurance the reference leads with: pressing Run must not read
 * as changing what the binding does from now on. And a way through to the run itself.
 *
 * The reference's version also says "the daily 06:00 schedule is unchanged, and on failure it still
 * raises an incident". Neither is said here: nothing in this platform schedules a rule run, so there
 * is no schedule to leave unchanged, and what happens on failure is the tenant's alert routing
 * rather than anything this binding decides. Claiming either would be the banner reassuring a reader
 * about a mechanism that is not there.
 */
function QueuedRun({
  queued,
  onDismiss,
}: {
  readonly queued: {
    readonly execution: { readonly id: string };
    readonly binding: RuleBinding;
  };
  readonly onDismiss: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const rules = useRules('');
  const rule = rules.data?.items.find((each) => each.id === queued.binding.rule_id);
  const target = queued.binding.selectors
    .map((selector) => selector.description)
    .join(' or ');

  return (
    <div className="lq-queued" role="status">
      <strong className="lq-queued__title">
        {t('rules.bindings.queued.title', { run: shortRunId(queued.execution.id) })}
      </strong>
      <p className="lq-queued__body">
        {t('rules.bindings.queued.body', {
          rule: rule?.name ?? t('rules.bindings.ruleUnknown'),
          target,
        })}
      </p>
      <span className="lq-queued__actions">
        {/* **The specific run, not the list.** "Open Executions" in the reference goes to the
            executions surface; going to the *run that was just queued* is what the reader means, and
            it is the screen that shows its tasks arriving. */}
        <Link to={`${SCREEN_PATHS.executions}/${queued.execution.id}`}>
          {t('rules.bindings.queued.open')}
        </Link>
        <button type="button" className="lq-rowaction" onClick={onDismiss}>
          {t('rules.bindings.queued.dismiss')}
        </button>
      </span>
    </div>
  );
}
